import { Component, type ErrorInfo, type ReactNode } from 'react';
import './error-boundary.css';

/**
 * 2026-07-20 傷②: root ErrorBoundary（apps/player/src に 0 件だった描画例外の受け皿）。
 *
 * ねらい: 描画中の未捕捉例外で React ツリー全体がアンマウントされて「無言の白画面」になる事故を、
 * シェルの声（S1: 端末の状態断片体）に沿った fallback + 読み直し導線へ置き換える。
 * - 文言はシェルの声規格（visual_grammar_and_foundation_guide §4.6）に従う:
 *   地の文は状態断片体（キャラ化しない）・三点リーダは「……」（2字1組）・英語 UI 語彙なし。
 * - 開発モード（import.meta.env.DEV）のみエラー詳細（stack）を表示する。本番は文言のみ。
 * - 色・書体は既存の --shell-* トークンのみ消費する（error-boundary.css。新規 hex なし＝
 *   test:direct-colors 準拠）。
 */

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // 白画面の代わりに fallback を出すが、原因調査のためコンソールには必ず残す。
    console.error('[ErrorBoundary] 描画中の未捕捉例外:', error, info.componentStack);
  }

  private handleReload = (): void => {
    // 進行保存（localStorage）は消さない。互換な進行があれば「続きから」に復帰できる。
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) {
      return this.props.children;
    }
    return (
      <div className="error-boundary" role="alert">
        <p className="error-boundary__message">……記録は、ここで途切れている。</p>
        <button type="button" className="error-boundary__reload" onClick={this.handleReload}>
          最初から読み直す
        </button>
        {import.meta.env.DEV && this.state.error ? (
          <pre className="error-boundary__detail">
            {String(this.state.error.stack ?? this.state.error)}
          </pre>
        ) : null}
      </div>
    );
  }
}
